import { motion, MotionProps } from 'framer-motion';

import { AnimatePresence } from 'framer-motion';
import { LocalThemeContextProvider, useLocalThemeContext } from '../context';
import { Dispatch, SetStateAction, useEffect, useId, useState } from 'react';
import clsx from 'clsx';
import {
  autoUpdate,
  FloatingPortal,
  safePolygon,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  Placement,
  Middleware,
  UseInteractionsReturn,
  UseFloatingReturn,
} from '@floating-ui/react';

interface WithInlineDeleteActionProps<E extends HTMLElement> {
  children: (props: {
    className: string;
    setReference: UseFloatingReturn<E>['refs']['setReference'];
    motionProps: Pick<MotionProps, 'animate' | 'transition' | 'onAnimationComplete' | 'initial'>;
    getFloatingReferenceProps: UseInteractionsReturn['getReferenceProps'];
    isDeleteActionOpen: boolean;
  }) => React.ReactNode;
  disabled?: boolean;
  deleteElement: React.ReactNode;
  deleteColor: string;
  onDeleteAnimationComplete?: () => void;
  currentPendingDeleteID: string | null;
  setCurrentPendingDeleteID: Dispatch<SetStateAction<string | null>>;
  actionMode: 'hover' | 'click';
  floatingPlacement: Placement;
  floatingMiddleware?: Middleware[];
}

export function WithInlineDeleteAction<E extends HTMLElement>({
  children,
  disabled,
  deleteColor,
  currentPendingDeleteID,
  setCurrentPendingDeleteID,
  onDeleteAnimationComplete,
  actionMode,
  floatingPlacement,
  deleteElement,
  floatingMiddleware,
}: WithInlineDeleteActionProps<E>) {
  // Access
  // useID will ensure any given instance of the component has a unique ID over its lifecycle
  const tagListID = useId();
  const { paletteSeedColor } = useLocalThemeContext();

  // State
  const [animationStarted, setAnimationStarted] = useState(false);
  const [animationFinished, setAnimationFinished] = useState(false);
  const [isDeleteActionOpen, setIsDeleteActionOpen] = useState(false);

  // Derived
  const enableClick = actionMode === 'click' && !disabled;
  const enableHover = actionMode === 'hover' && !disabled;

  // Floating UI
  const {
    refs,
    floatingStyles,
    context: floatingContext,
  } = useFloating<E>({
    placement: floatingPlacement,
    middleware: floatingMiddleware,
    open: isDeleteActionOpen,
    whileElementsMounted: autoUpdate,
    onOpenChange: setIsDeleteActionOpen,
  });
  const hover = useHover(floatingContext, { handleClose: safePolygon(), enabled: enableHover });
  const click = useClick(floatingContext, { toggle: true, enabled: enableClick, keyboardHandlers: true });
  const dismiss = useDismiss(floatingContext, { enabled: enableClick, outsidePress: true, outsidePressEvent: 'click' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, dismiss]);

  // Functions
  function handleAnimationComplete() {
    onDeleteAnimationComplete?.();
    setAnimationFinished(true);
  }

  // Effects

  // Manages the currently "pending delete" item in the parent component.
  // We set the ID when the delete action becomes visible (but before animation),
  // and clear it when either the animation starts or the delete action closes.
  // This allows other items to be styled while one is being proposed for deletion.
  useEffect(() => {
    if (isDeleteActionOpen && !animationStarted) {
      setCurrentPendingDeleteID(tagListID);
    } else {
      setCurrentPendingDeleteID(null);
    }
  }, [isDeleteActionOpen, animationStarted, tagListID, setCurrentPendingDeleteID]);

  // If the animation has finished, we don't need to render the component
  if (animationFinished) return null;

  return (
    <>
      <LocalThemeContextProvider<E> paletteOverrides={{ 600: deleteColor }} themeTargetRef={refs.domReference}>
        {children({
          setReference: refs.setReference,
          getFloatingReferenceProps: getReferenceProps,
          motionProps: {
            transition: { duration: 0.3 },
            onAnimationComplete: handleAnimationComplete,
            animate: animationStarted && { height: 0, padding: 0 },
          },
          isDeleteActionOpen,
          className: clsx(
            animationStarted && [
              '[&_*]:!text-palette-600 [&_*]:!bg-palette-600 [&_*]:!border-palette-600 [&_*]:!fill-palette-600 [&_*]:!stroke-palette-600 [&_*]:!outline-palette-600 [&_*]:!accent-palette-600 [&_*]:!caret-palette-600',
              '!bg-palette-600 !min-h-[auto] !overflow-hidden !text-palette-600 !border-palette-600 !fill-palette-600 !stroke-palette-600 !outline-palette-600 !accent-palette-600 !caret-palette-600',
            ],
            actionMode === 'click' && [
              'transition-transform duration-200',
              isDeleteActionOpen && 'dark:!shadow-classic !shadow-lightModeClassic !rounded-2xl !scale-[1.05]',
              currentPendingDeleteID && currentPendingDeleteID !== tagListID && '!opacity-20 !pointer-events-none',
            ]
          ),
        })}
      </LocalThemeContextProvider>
      <AnimatePresence>
        {isDeleteActionOpen && !animationStarted && !disabled && (
          // We need to "forward" the paletteSeedColor to the children of the FloatingPortal
          // because they escape the tree (by being directly appended to the body tag) therefore any ancestor
          // LocalThemeContextProvider will not be able to provide the paletteSeedColor (via context) to the children of the FloatingPortal
          <FloatingPortal>
            <LocalThemeContextProvider paletteSeedColor={paletteSeedColor}>
              <motion.div
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}
                onClick={() => setAnimationStarted(true)}
                className={clsx(disabled && 'pointer-events-none', 'z-[100] grid place-content-center')}
              >
                {deleteElement}
              </motion.div>
            </LocalThemeContextProvider>
          </FloatingPortal>
        )}
      </AnimatePresence>
    </>
  );
}
